'use client';

import { IconFileTypePdf, IconLoader2 } from '@tabler/icons-react';
import { RenderDocumentResponseBody } from 'bff';
import { useTranslations } from 'i11n';
import { useCallback, useRef, useState } from 'react';
import {
  buttonVariants,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'ui';
import { WretchError } from 'wretch/resolver';

/**
 * Genera un PDF y lo entrega recién cuando está listo.
 *
 * El render tarda segundos, y abrir la pestaña destino antes de tenerlo —la
 * única forma de que el navegador no la bloquee como popup— deja un
 * `about:blank` que el usuario lee como que la app se colgó. Acá la espera pasa
 * a un diálogo con spinner y la pestaña se abre con el click sobre el link, que
 * ya es un gesto real.
 *
 * `children` es el trigger; el endpoint que genera el documento se inyecta
 * porque el cliente HTTP es de cada app.
 */
export const DownloadDocumentDialog = ({
  children,
  render,
  onError,
}: {
  children: React.ReactNode;
  render: () => Promise<RenderDocumentResponseBody>;
  onError: (error: WretchError) => unknown;
}) => {
  const t = useTranslations();

  const [open, setOpen] = useState(false);
  const [pdf, setPdf] = useState<RenderDocumentResponseBody>();

  // Cada apertura y cada cierre invalidan lo que haya en vuelo: sin esto, un
  // render que resuelve después de cerrar deja el diálogo "listo" la próxima vez
  // que se abra, con un PDF que puede estar desactualizado.
  const renderId = useRef(0);

  const closeDialog = useCallback(() => setOpen(false), []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      renderId.current += 1;
      setPdf(undefined);
      setOpen(next);

      if (!next) return;

      const id = renderId.current;

      render()
        .then((result) => {
          if (id === renderId.current) setPdf(result);
        })
        .catch((error) => {
          if (id !== renderId.current) return;

          setOpen(false);
          onError(error);
        });
    },
    [onError, render],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>
            {pdf
              ? t('document.download.ready_title')
              : t('document.download.generating_title')}
          </DialogTitle>
          <DialogDescription>
            {pdf
              ? t('document.download.ready_description')
              : t('document.download.generating_description')}
          </DialogDescription>
        </DialogHeader>

        {pdf ? (
          <div className='flex flex-col gap-4'>
            <div className='flex flex-row items-center gap-2 rounded-xl border p-3'>
              <IconFileTypePdf className='h-5 w-5 shrink-0 text-primary' />
              <span className='truncate text-sm'>{pdf.original_name}</span>
            </div>
            {/* El link va estilado con `buttonVariants` y no con `<Button asChild>`:
                el Button mete un spinner condicional y un `{!loading}` suelto
                como hermanos, y el Slot de Radix exige un único hijo. Es el
                mismo patrón que ya usan OrderWorkProofSection y NetworkDataTable. */}
            <a
              className={cn(buttonVariants({ size: 'sm' }))}
              href={pdf.url}
              target='_blank'
              rel='noopener noreferrer'
              onClick={closeDialog}
            >
              {t('document.download.open')}
            </a>
          </div>
        ) : (
          <div className='flex items-center justify-center py-8'>
            <IconLoader2 className='h-8 w-8 animate-spin text-primary' />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
